import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";
import RightTrapezoidDiagram from "../../../../components/diagrams/RightTrapezoidDiagram";

const DEC_TOL = 0.01;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function squareFactorOut(n) {
  let r = Math.max(0, Math.round(n));
  let s = 1;
  for (let p = 2; p * p <= r; p++) {
    while (r % (p * p) === 0) {
      r = Math.floor(r / (p * p));
      s *= p;
    }
  }
  return [s, r];
}

export function generateTask() {
  // a — GÓRNA, b — DOLNA; wymagamy b > a
  const As = [6, 7, 8, 9, 10, 11, 12];
  const Bs = [8, 9, 10, 11, 12, 13, 14, 15, 16];
  const Hs = [3, 4, 5, 6, 7, 8, 9];

  for (let t = 0; t < 200; t++) {
    const a = pick(As);
    const candidatesB = Bs.filter((v) => v > a);
    if (!candidatesB.length) continue;
    const b = pick(candidatesB);
    const h = pick(Hs);
    return { type: "TrapezoidDiagonalRightTrapezoidTask", data: { a, b, h } };
  }
  return { type: "TrapezoidDiagonalRightTrapezoidTask", data: { a: 8, b: 10, h: 6 } };
}

export function renderTask({ data }) {
  const { a, b, h } = data;

  return (
    <div style={{ marginTop: 8, marginBottom: 8, display: "grid", gap: 10 }}>
      <div>
        Dany jest trapez <b>prostokątny</b> o podstawach{" "}
        <InlineMath math={`a=${a}`} /> (górna),{" "}
        <InlineMath math={`b=${b}`} /> (dolna) i wysokości{" "}
        {/* spinamy "h=... ." w jeden niełamliwy kawałek */}
        <span style={{ whiteSpace: "nowrap" }}>
          <InlineMath math={`h=${h}`} />
          {"."}
        </span>{" "}
        Oblicz długości przekątnych <InlineMath math={"d_1"} /> oraz{" "}
        <InlineMath math={"d_2"} />.{" "}
        <i>(Kolejność ma znaczenie — patrz rysunek poniżej.)</i>
      </div>

      {/* Rysunek w treści zadania: z wartościami i podpisanymi przekątnymi */}
      <div style={{ display: "grid", placeItems: "center", marginTop: 6 }}>
        <div style={{ width: "min(100%, 420px)" }}>
          <RightTrapezoidDiagram a={a} b={b} h={h} showValues={true} showDiagonals />
        </div>
      </div>

      <div style={{ opacity: 0.95 }}>
        Punktacja: <b>2 pkt</b> (po <b>1 pkt</b> za każdą poprawnie obliczoną przekątną).
      </div>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  const val = (k) => (typeof userAnswer === "object" && userAnswer?.[k]) || "";
  const set = (k, v) => onChange({ ...(userAnswer || {}), [k]: v });

  // Układ jak w SetOps: podpis NAD inputem, żeby było oczywiste co jest czym.
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ marginLeft: 2 }}>
          <InlineMath math={"d_1="} />
        </div>
        <RootFractionInput
          value={val("d1")}
          onChange={(v) => set("d1", v)}
          disabled={disabled}
          label="d_1" // podpis mamy nad inputem
          placeholder="np. 5√2"
        />
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ marginLeft: 2 }}>
          <InlineMath math={"d_2="} />
        </div>
        <RootFractionInput
          value={val("d2")}
          onChange={(v) => set("d2", v)}
          disabled={disabled}
          label="d_2" // podpis mamy nad inputem
          placeholder="np. 3√5"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { a, b, h } = data;

  // W układzie: d₁ zależy od b, d₂ od a (jak na diagramie)
  const expectedD1 = Math.sqrt(b * b + h * h); // d₁: LT→BR
  const expectedD2 = Math.sqrt(a * a + h * h); // d₂: LB→TR

  const parseMaybe = (x) => {
    const raw = typeof x === "object" ? x.raw : x;
    if (typeof raw !== "string") return null;
    let parsed = parseRootFractionInput(raw);
    if (!Number.isFinite(parsed)) {
      const asNumber = Number(raw.replace(",", "."));
      if (Number.isFinite(asNumber)) parsed = asNumber;
    }
    return Number.isFinite(parsed) ? parsed : null;
  };

  const u1 = parseMaybe(userAnswer?.d1);
  const u2 = parseMaybe(userAnswer?.d2);

  const d1Ok = u1 != null && Math.abs(u1 - expectedD1) <= DEC_TOL;
  const d2Ok = u2 != null && Math.abs(u2 - expectedD2) <= DEC_TOL;

  const score = (d1Ok ? 1 : 0) + (d2Ok ? 1 : 0);

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: { d1: d1Ok, d2: d2Ok },
  };
}

/* --- odpowiedź: s√r (bez „1√…”) i z właściwym przypisaniem --- */
export const renderAnswer = ({ data }) => {
  const { a, b, h } = data;
  const fmt = (R) => {
    const [s, r] = squareFactorOut(R);
    if (r === 1) return `${s}`;
    if (s === 1) return `\\sqrt{${r}}`;
    return `${s}\\,\\sqrt{${r}}`;
  };
  const d1 = fmt(b * b + h * h); // d₁ ~ b
  const d2 = fmt(a * a + h * h); // d₂ ~ a

  return (
    <span>
      <InlineMath math={`d_1=${d1}`} />{" "}
      <InlineMath math={`\\;\\;\\; d_2=${d2}`} />
    </span>
  );
};

export function renderExplanation({ data }) {
  const { a, b, h } = data;

  const a2 = a * a, b2 = b * b, h2 = h * h;
  const R1 = b2 + h2; // d₁
  const R2 = a2 + h2; // d₂

  const fmt = (R) => {
    const [s, r] = squareFactorOut(R);
    return r === 1 ? `${s}` : (s === 1 ? `\\sqrt{${r}}` : `${s}\\,\\sqrt{${r}}`);
  };

  const marginTopPx = 14, marginBottomPx = 6;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, display: "grid", gap: 10 }}>
      <div><strong>Krok 1.</strong> Schemat (z danymi):</div>
      <div style={{ marginTop: marginTopPx, marginBottom: marginBottomPx }}>
        <div style={{ display: "grid", placeItems: "center" }}>
          <div style={{ width: "min(100%, 420px)" }}>
            <RightTrapezoidDiagram a={a} b={b} h={h} showValues={true} showDiagonals />
          </div>
        </div>
      </div>

      <div><strong>Krok 2.</strong> Twierdzenie Pitagorasa dla obu przekątnych:</div>
      <BlockMath math={'d_1^2=b^2+h^2\\quad\\Rightarrow\\quad d_1=\\sqrt{b^2+h^2}'} />
      <BlockMath math={'d_2^2=a^2+h^2\\quad\\Rightarrow\\quad d_2=\\sqrt{a^2+h^2}'} />

      <div><strong>Krok 3.</strong> Podstawiamy dane i liczymy krok po kroku:</div>
      <BlockMath math={`d_1=\\sqrt{${b}^2+${h}^2}=\\sqrt{${b2}+${h2}}=${fmt(R1)}`} />
      <BlockMath math={`d_2=\\sqrt{${a}^2+${h}^2}=\\sqrt{${a2}+${h2}}=${fmt(R2)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const r1 = typeof userAnswer?.d1 === "object" ? userAnswer.d1.raw : userAnswer?.d1;
  const r2 = typeof userAnswer?.d2 === "object" ? userAnswer.d2.raw : userAnswer?.d2;
  const empty1 = !r1 || (typeof r1 === "string" && r1.trim() === "");
  const empty2 = !r2 || (typeof r2 === "string" && r2.trim() === "");
  if (empty1 && empty2) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {!empty1 && <InlineMath math={formatLatexPreview(r1, "d_1")} />}
      {!empty2 && <InlineMath math={formatLatexPreview(r2, "d_2")} />}
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
